// Defines Store Action UI message keys and formatting helpers over the shared i18next instance.
import i18n from 'i18next';

const MESSAGE_KEYS = {
  heading: 'Store Action Items',
  loading: 'Loading action items',
  pendingEmpty: 'No pending action items',
  criteriaEmpty: 'No action items match this category',
  activeList: 'Pending action items',
  categoryFilter: 'Action category',
  categoryAll: 'All',
  categoryAgreement: 'Agreement',
  categoryInventory: 'Inventory',
  categoryCustomer: 'Customer',
  categoryInvoice: 'Invoice',
  categoryOther: 'Other',
  categoryVerification: 'Verification',
  categoryInitialPayment: 'Initial Payment',
  categoryDelivery: 'Delivery',
  searchActionItems: 'Search Action Items',
  clearSearch: 'Clear search',
  actionStatus: 'Action status',
  completedEmpty: 'No completed action items',
  dateCreated: 'Date Created',
  description: 'Description',
  type: 'Type',
  typeOther: 'Other',
  status: 'Status',
  statusActive: 'Active',
  statusCompleted: 'Completed',
  expand: 'Expand',
  closeExpandedView: 'Close expanded view',
  toDoItems: 'To Do Items',
  completedItems: 'Completed Items',
  actions: 'Actions',
  loadMore: 'Load more',
  loadingMore: 'Loading more action items',
  refreshingItems: 'Refreshing Items...',
  unavailable: 'Store Action Items unavailable',
  unableToLoad: 'Unable to load action items',
  tryAgainSoon: 'Try again in a moment.',
  temporarilyUnavailable: 'The application is temporarily unavailable',
  actionUnavailable: 'This action item is unavailable',
  retry: 'Retry',
  cancel: 'Cancel',
  markComplete: 'Mark Complete',
  markIncomplete: 'Mark Incomplete',
  confirmCompleteTitle: 'Complete action item',
  confirmCompleteMessage:
    'Are you sure you want to mark this action item complete?',
  confirmReopenTitle: 'Reopen action item',
  confirmReopenMessage:
    'Are you sure you want to mark this action item incomplete?',
  transferLoading: 'Loading transfer details',
  transferOutTitle: 'eCommerce Transfer OUT',
  transferInTitle: 'eCommerce Transfer IN',
  transferOutCustomer:
    'An eCommerce Agreement was created for Customer {{customerName}} with inventory # {{itemNumber}} from your store. The inventory was auto transferred to store {{receivingStoreNumber}} to pick-up the inventory.',
  transferOutSalesLead:
    'A Sales lead was created with inventory # {{itemNumber}} from your store. The inventory was auto transferred to store {{receivingStoreNumber}} to pick-up the inventory.',
  transferInCustomer:
    'eCommerce Customer {{customerName}} with inventory # {{itemNumber}} was auto transferred to your store. Please make arrangements to pick-up the inventory located at store {{transferringStoreNumber}}.',
  transferInSalesLead:
    'A Sales lead was created with inventory # {{itemNumber}} was auto transferred to your store. Please make arrangements to pick-up the inventory located at store {{transferringStoreNumber}}.',
  transferItem: 'Item',
  transferModel: 'Model',
  transferVendor: 'Vendor',
  transferBrand: 'Brand',
  transferSerial: 'MFG Serial #',
  transferSender: 'Sending Store',
  transferReceiver: 'Receiving Store',
  print: 'Print',
  transferOutForm: 'Transfer Out Form',
  transferInForm: 'Transfer Received Form',
  page: 'Page',
  transferSoldNotice: 'I Am Sold!',
  deliveredBy: 'Delivered By',
  receivedBy: 'Received By',
  ok: 'OK',
};

type StoreActionMessageKey = keyof typeof MESSAGE_KEYS;

export const getStoreActionMessage = (key: StoreActionMessageKey): string => {
  const message = i18n.t(MESSAGE_KEYS[key], { ns: 'dashboard' });

  return typeof message === 'string' && message ? message : MESSAGE_KEYS[key];
};

const DESCRIPTION_PHRASES_BY_TYPE: Readonly<
  Record<string, ReadonlyArray<string>>
> = {
  CD: ['Pending delivery confirmation'],
  ITWTI: ['Transfer request for inventory', 'from store'],
  ITWTO: ['Transfer request for inventory', 'awaiting approval from the store'],
  AWT: ['Transfer for', 'is pending'],
  PDPA: ['Agreement pending for'],
  EATNDS: ['Schedule Customer Order delivery', 'for store'],
  SOADS: ['Schedule Customer Order delivery', 'for store'],
  ATNTBS: ['Digital agreement', 'is not signed for'],
  MRR: ['Make rent ready. Inventory #:'],
  AER: ['Complete Return for the Exchange Agreement'],
  CSFD: ['DOF Submitted for'],
  CVREVPEN: ['A verification is pending review for'],
  SOABC: ['TireBuyer Order cancelled, please cancel agreement'],
  SOABP: ['Need payment. Customer order agreement'],
  EAITT: ['eComm transfer pick-up inventory', 'for eComm Customer from store'],
  EAITF: ['eComm inventory', 'auto transferred to store'],
  TBINSCOM: ['Tire Buyer Installation Completed for customer', 'and store'],
  TBINSCAN: ['Tire buyer installation cancelled'],
  TBINSMIS: ['Tire buyer installation missed'],
  DSS: ['Survey Pending for'],
  TCRS: ['Approval is Pending for'],
  PRINT_EDIT_PRICETAG: ['Print Tag(s) in Print Queue'],
};

const translatePhrase = (phrase: string): string => {
  const translated = i18n.t(phrase, { ns: 'dashboard' });

  return typeof translated === 'string' && translated ? translated : phrase;
};

export const localizeStoreActionDescription = (
  storeActionType: string,
  description: string
): string => {
  const phrases = DESCRIPTION_PHRASES_BY_TYPE[storeActionType];
  if (!phrases || !description) return description;

  return phrases.reduce(
    (localizedDescription, phrase) =>
      localizedDescription.split(phrase).join(translatePhrase(phrase)),
    description
  );
};
